import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { jsonLd } from '@/components/SEO';
import FAQ, { type FAQItem } from '@/components/FAQ';
import { Panel, ReadingList, Table, TD, TH, ToolIntro, ToolSection } from '@/components/ToolKit';
import { formatDate } from '@/lib/format';
import { AUTHOR, ORACLE_FACTS, ORACLE_LIFECYCLE, SITE_NAME, SITE_ORIGIN, absoluteUrl } from '@/lib/site';

// The citable dates table. Bump LAST_REVIEWED whenever a row is re-verified
// or changed; it is the visible "Last reviewed" line and the JSON-LD
// dateModified. Dates are carried from the source table as published; the
// only additions are the site-relative links and the estate filter.
const FIRST_PUBLISHED = '2026-07-07';
const LAST_REVIEWED = ORACLE_LIFECYCLE.checked;
const PATH = '/tools/oracle-fusion-middleware-support-dates';

const ORACLE_SOURCES = [
  {
    label: 'Oracle Lifetime Support Policy: Oracle Fusion Middleware (PDF)',
    url: ORACLE_LIFECYCLE.source,
    detail: 'The primary source. Product-by-product Premier, Extended and Sustaining Support dates, maintained by Oracle.',
  },
  {
    label: 'Oracle Fusion Middleware: end of support information (docs.oracle.com)',
    url: 'https://docs.oracle.com/en/middleware/fusion-middleware/endsupport.html',
    detail: "Oracle's documentation entry point for Fusion Middleware support timelines and upgrade guidance.",
  },
];

interface SupportRow {
  id: string;
  component: string;
  products: string;
  premierEnds: string;
  extendedEnds: string;
  path14c: string;
  window14c: string;
  href: string;
  hrefLabel: string;
  noPath?: boolean;
}

const SUPPORT_ROWS: SupportRow[] = [
  {
    id: 'webcenter',
    component: 'Oracle WebCenter',
    products: 'Content (UCM), Imaging, Enterprise Capture, Portal and Sites. Confirm WFR separately by its own build and runtime.',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/webcenter-14c-upgrade',
    hrefLabel: 'WebCenter 14c upgrade',
  },
  {
    id: 'soa',
    component: 'Oracle SOA Suite',
    products: 'SOA composites, adapters, B2B',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-soa-suite-12c-to-14c-upgrade',
    hrefLabel: 'SOA Suite 12c to 14c',
  },
  {
    id: 'weblogic',
    component: 'Oracle WebLogic Server',
    products: 'The application server the Fusion Middleware stack runs on',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-weblogic-14c-upgrade',
    hrefLabel: 'WebLogic 14c upgrade',
  },
  {
    id: 'bpm',
    component: 'Oracle BPM Suite',
    products: 'Business-process workflows (alongside SOA Suite)',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-bpm-suite-12c-to-14c-upgrade',
    hrefLabel: 'BPM Suite 12c to 14c',
  },
  {
    id: 'adf',
    component: 'Oracle ADF',
    products: 'Application Development Framework customizations and forms',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/migrations/adf-non-po-coding-form-modernization',
    hrefLabel: 'ADF non-PO coding form',
  },
  {
    id: 'forms',
    component: 'Oracle Forms and Reports',
    products: 'Classic Forms applications',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c ${ORACLE_FACTS.forms14c}`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-forms-12c-to-14c-upgrade',
    hrefLabel: 'Forms 12c to 14c upgrade',
  },
  {
    id: 'iam',
    component: 'Oracle Identity and Access Management',
    products: 'OAM, OIG, OUD, OID',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c ${ORACLE_FACTS.iam14c}`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-identity-management-12c-to-14c-upgrade',
    hrefLabel: 'IAM 12c to 14c upgrade',
  },
  {
    id: 'odi',
    component: 'Oracle Data Integrator (ODI)',
    products: 'ETL and data-integration repositories and agents',
    premierEnds: ORACLE_FACTS.fmw12cPremierEnds,
    extendedEnds: ORACLE_FACTS.fmw12cExtendedEnds,
    path14c: `14c (${ORACLE_FACTS.release14c})`,
    window14c: `Premier to ${ORACLE_FACTS.fmw14cPremierEnds} · Extended to ${ORACLE_FACTS.fmw14cExtendedEnds}`,
    href: '/upgrades/oracle-data-integrator-12c-to-14c-upgrade',
    hrefLabel: 'ODI 12c to 14c upgrade',
  },
  {
    id: 'obiee',
    component: 'OBIEE / Oracle BI 12c',
    products: 'Business Intelligence Enterprise Edition',
    premierEnds: 'Ended',
    extendedEnds: 'Ended August 2025',
    path14c: 'No 14c release. The forward path is Oracle Analytics Server (OAS) or Oracle Analytics Cloud (OAC).',
    window14c: '—',
    href: '/upgrades/oracle-fusion-middleware-12c-end-of-support',
    hrefLabel: 'Full 12c component map',
    noPath: true,
  },
  {
    id: 'endeca',
    component: 'Oracle Endeca',
    products: 'Search and guided discovery',
    premierEnds: 'Not listed here; confirm with Oracle',
    extendedEnds: '—',
    path14c: 'No 14c release. A replacement search or discovery platform is needed.',
    window14c: '—',
    href: '/upgrades/oracle-fusion-middleware-12c-end-of-support',
    hrefLabel: 'Full 12c component map',
    noPath: true,
  },
];

const PHASES = [
  {
    title: 'Premier Support',
    body: `Full support: bug fixes, security updates and new operating-system and database certifications, with new features where a release carries them. For Fusion Middleware 12c (12.2.1.4) it ends ${ORACLE_FACTS.fmw12cPremierEnds}.`,
  },
  {
    title: 'Extended Support',
    body: `Paid Extended Support includes updates, fixes and security coverage subject to the applicable product and error-correction policies. Certification coverage differs from Premier Support. For applicable 12.2.x products, the published window ends ${ORACLE_FACTS.fmw12cExtendedEnds}; confirm your release and entitlement with Oracle.`,
  },
  {
    title: 'Sustaining Support',
    body: 'Runs after Extended Support, indefinitely. Access to the patches, fixes and documentation that already exist; no new patches, no new security updates, no new certifications.',
  },
  {
    title: 'Market Driven Support',
    body: 'A yearly-renewable program Oracle has offered for selected releases beyond the Extended Support boundary. Availability, scope and terms are set by Oracle per release; confirm the current terms for 12.2.1.4 with Oracle.',
  },
];

const FAQS: FAQItem[] = [
  {
    q: 'When does Oracle Fusion Middleware 12c support end?',
    a: `Premier Support for Fusion Middleware 12c (12.2.1.4) ends ${ORACLE_FACTS.fmw12cPremierEnds}; Extended Support ends ${ORACLE_FACTS.fmw12cExtendedEnds}. The applicable 12.2.x family and product listings cover: WebCenter, SOA Suite, WebLogic Server, BPM Suite, ADF, Forms and Reports, Identity and Access Management and Data Integrator. After December 2027 the standard position is Sustaining Support, which adds no new patches or certifications.`,
  },
  {
    q: 'Which Fusion Middleware 12c components have a 14c release?',
    a: `WebCenter, SOA Suite, WebLogic Server, BPM Suite, ADF and Data Integrator have ${ORACLE_FACTS.release14c} releases. Oracle Forms 14c is ${ORACLE_FACTS.forms14c} and Identity and Access Management 14c is ${ORACLE_FACTS.iam14c}. OBIEE and Endeca have no 14c release: OBIEE moves to Oracle Analytics Server or Oracle Analytics Cloud, and Endeca needs a replacement search platform.`,
  },
  {
    q: 'From which 12c version can I upgrade to 14c?',
    a: 'Starting releases are component-specific. WebCenter Content 14.1.2 documents a 12.2.1.4.0 starting point; other products have their own supported paths. Confirm the upgrade guide and certification matrix for each installed component before choosing intermediate steps.',
  },
  {
    q: 'Where does Oracle publish the official support dates?',
    a: 'In the Oracle Lifetime Support Policy for Oracle Fusion Middleware, a PDF Oracle maintains at oracle.com, and on the Fusion Middleware end-of-support page at docs.oracle.com. Both are linked from this page. This table is a consolidated reference; Oracle’s documents are authoritative, and if they change, they govern.',
  },
];

const OracleFusionMiddlewareSupportDates = () => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const filtering = selected.size > 0;
  const rows = useMemo(
    () => (filtering ? SUPPORT_ROWS.filter((r) => selected.has(r.id)) : SUPPORT_ROWS),
    [filtering, selected],
  );
  const with14c = rows.filter((r) => !r.noPath).length;
  const without14c = rows.length - with14c;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle Fusion Middleware support dates, 12c to 14c',
    description:
      'Every Oracle Fusion Middleware 12c component with its Premier and Extended Support end dates, its 14c path or replacement path, and the 14c support window, consolidated from the Oracle Lifetime Support Policy.',
    datePublished: FIRST_PUBLISHED,
    dateModified: LAST_REVIEWED,
    inLanguage: 'en-US',
    url: absoluteUrl(PATH),
    mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(PATH) },
    author: { '@type': 'Person', name: AUTHOR.name, jobTitle: AUTHOR.jobTitle },
    publisher: { '@type': 'Organization', '@id': `${SITE_ORIGIN}/#organization`, name: SITE_NAME },
    about: [
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 12c' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 14c' },
      { '@type': 'Thing', name: 'Oracle WebCenter' },
      { '@type': 'Thing', name: 'Oracle Lifetime Support Policy' },
    ],
  };

  return (
    <>
      <Head>
        <script type="application/ld+json">{jsonLd(articleSchema)}</script>
      </Head>

      <ToolIntro>
        <p>
          Oracle publishes these dates across a policy PDF, documentation pages and support notes. This table puts them
          in one place: the {ORACLE_FACTS.fmw12cPremierEnds} and {ORACLE_FACTS.fmw12cExtendedEnds} boundaries for
          Fusion Middleware 12c, the 14c path (or replacement path) for each component, and the 14c support window.
          Tick the components in your estate to narrow the table to what applies to you.
        </p>
        <p className="text-small">
          Last reviewed <time dateTime={LAST_REVIEWED}>{formatDate(LAST_REVIEWED)}</time>. Confirm every date against
          the current Oracle Lifetime Support Policy before you plan around it. The reviewed document is effective {formatDate(ORACLE_LIFECYCLE.policyEffective)}.
        </p>
      </ToolIntro>

      <ToolSection id="estate" title="Your estate">
        <Panel>
          <p className="text-body">Select the components you run. Leave everything unticked to see the whole table.</p>
          <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORT_ROWS.map((r) => (
              <li key={r.id}>
                <label className="flex cursor-pointer items-start gap-3 text-small">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                    className="mt-1 h-5 w-5 shrink-0 accent-[#1F3329]"
                  />
                  <span>{r.component}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-rule pt-5">
            <p className="text-body" role="status">
              {filtering ? (
                <>
                  <strong>{rows.length}</strong> selected: <strong>{with14c}</strong> with a 14c release,{' '}
                  <strong>{without14c}</strong> needing a replacement path.
                </>
              ) : (
                <>
                  Showing all <strong>{SUPPORT_ROWS.length}</strong> components: <strong>{with14c}</strong> with a 14c
                  release, <strong>{without14c}</strong> needing a replacement path.
                </>
              )}
            </p>
            {filtering && (
              <button type="button" className="link text-small" onClick={() => setSelected(new Set())}>
                Clear selection
              </button>
            )}
          </div>
        </Panel>
      </ToolSection>

      <ToolSection id="table" title="Support dates by Fusion Middleware 12c component">
        <Table caption="Oracle Fusion Middleware 12c components: support end dates, 14c path and 14c support window">
          <thead>
            <tr>
              <TH>Component</TH>
              <TH>12c Premier ends</TH>
              <TH>12c Extended ends</TH>
              <TH>14c path</TH>
              <TH>14c support window</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <TD>
                  <span className="font-bold">{r.component}</span>
                  <span className="mt-1 block text-[0.9375rem] leading-[1.4] text-forest/80">{r.products}</span>
                  <Link to={r.href} className="link mt-2 inline-block text-[0.9375rem] font-bold">
                    {r.hrefLabel}
                  </Link>
                </TD>
                <TD nowrap>{r.premierEnds}</TD>
                <TD nowrap>{r.extendedEnds}</TD>
                <TD>{r.noPath ? <span className="border-l-[3px] border-l-brass pl-3">{r.path14c}</span> : r.path14c}</TD>
                <TD>{r.window14c}</TD>
              </tr>
            ))}
          </tbody>
        </Table>
        <p className="mt-5 max-w-[46rem] text-small text-forest/80">
          Starting releases and upgrade procedures are component-specific; confirm each component’s documented path.
          The 14c windows shown are as published at the last review; confirm against the current Oracle Lifetime
          Support Policy. For the component-by-component decision map, see the{' '}
          <Link to="/upgrades/oracle-fusion-middleware-12c-end-of-support" className="link">
            Fusion Middleware 12c end-of-support guide
          </Link>
          .
        </p>
      </ToolSection>

      <ToolSection id="reading" title="How to read the result">
        <p className="max-w-[46rem] text-body">
          The policy defines Premier, Extended and Sustaining Support. Separately offered support programs require
          their own confirmation of availability and terms.
        </p>
        <div className="mt-6">
          <ReadingList items={PHASES.map((p) => ({ title: p.title, body: p.body }))} />
        </div>
        <p className="mt-5 max-w-[46rem] text-body">
          Weighing Sustaining Support against an upgrade? See{' '}
          <Link to="/support/webcenter-sustaining-support-decision" className="link">
            the WebCenter Sustaining Support decision
          </Link>{' '}
          and{' '}
          <Link to="/notes/oracle-sustaining-support-vs-extended-support-12c" className="link">
            Sustaining versus Extended Support for 12c
          </Link>
          .
        </p>
      </ToolSection>

      <ToolSection id="sources" title="Oracle’s primary sources">
        <p className="max-w-[46rem] text-body">
          This page is a consolidated reference. Oracle’s documents are authoritative: if Oracle revises a date, their
          documents govern and this table is updated.
        </p>
        <ul className="mt-6 border-t border-rule">
          {ORACLE_SOURCES.map((s) => (
            <li key={s.url} className="border-b border-rule py-5">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="link text-small">
                {s.label}
              </a>
              <p className="mt-1 text-body">{s.detail}</p>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-small text-forest/80">
          Further reading:{' '}
          <Link to="/notes/oracle-fusion-middleware-12c-end-of-support-dates" className="link">
            the 12c end-of-support dates explained
          </Link>{' '}
          ·{' '}
          <Link to="/notes/oracle-webcenter-in-2026" className="link">
            Oracle WebCenter in 2026
          </Link>
        </p>
      </ToolSection>

      <div className="mt-16 max-w-[46rem]">
        <FAQ items={FAQS} />
      </div>
    </>
  );
};

export default OracleFusionMiddlewareSupportDates;
